import { JSX, useCallback, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { interviewActions } from 'src/redux/slices/Interview/interviewSlice';
import { useIPE } from 'src/redux/slices/Interview/hooks';
import type { InterviewState } from 'src/redux/slices/Interview/types';
import ContentCard from '../components/ContentCard';
import SectionLabel from '../components/SectionLabel';
import InterviewFooter from '../components/InterviewFooter';

export default function AddAnotherScreen(): JSX.Element {
  const dispatch = useDispatch();
  const { callIPE } = useIPE();
  const { currentGroup, deferred, history } = useSelector(
    (state: { interview: InterviewState }) => state.interview
  );

  const [selection, setSelection] = useState<'yes' | 'no' | null>(null);

  const groupName = currentGroup?.group_name ?? 'item';

  // Back-navigation: dropping the cursor below history.length flips
  // WorkItemRenderer into reviewing mode; navigating forward again returns
  // here (workItemType is still 'add_another' at the frontier).
  const showBack = history.length > 0;
  const handleBack = useCallback(() => {
    dispatch(interviewActions.moveCursorBack());
  }, [dispatch]);

  const handleSave = (): void => {
    if (selection === 'yes') {
      dispatch(interviewActions.startNewGroupInstance());
    } else {
      dispatch(interviewActions.setLoading(true));
      callIPE({
        skippedQitIds: [...new Set(Object.values(deferred.qitIds).flat())],
      });
    }
  };

  const options: Array<{ id: 'yes' | 'no'; label: string }> = [
    { id: 'yes', label: 'Yes, add another' },
    { id: 'no', label: 'No, I\u2019m done with this' },
  ];

  return (
    <Box
      data-testid="add-another-screen"
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
    >
      {/* Body — scrollable, centers content on desktop */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: { md: 'center' },
          alignItems: { md: 'flex-start' },
          pt: { md: 4 },
        }}
      >
        <ContentCard>
          <SectionLabel label={groupName} />
          <Typography sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.3 }}>
            Do you have another {groupName} to add?
          </Typography>
          <Typography
            sx={{ fontSize: 14, color: 'text.secondary', lineHeight: 1.5 }}
          >
            Select Yes if you have another {groupName} to include in your
            application.
          </Typography>
          {/* Radio option cards */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {options.map((option) => {
              const isSelected = selection === option.id;
              return (
                <Box
                  key={option.id}
                  onClick={() => setSelection(option.id)}
                  data-testid={`add-option-${option.id}`}
                  sx={{
                    height: 56,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.75,
                    px: 2.5,
                    borderRadius: '12px',
                    border: isSelected ? '2px solid' : '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    bgcolor: 'background.paper',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: isSelected ? 'primary.main' : 'grey.400',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      flexShrink: 0,
                      ...(isSelected
                        ? { bgcolor: 'primary.main' }
                        : { border: '2px solid', borderColor: 'grey.300' }),
                    }}
                  />
                  <Typography fontWeight={isSelected ? 600 : 'normal'}>
                    {option.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          {/* Desktop-only inline action buttons */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            {showBack && (
              <Button
                onClick={handleBack}
                data-testid="add-back-button-inline"
                sx={{
                  height: 44,
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: 'grey.300',
                  color: 'text.secondary',
                  fontSize: 14,
                  fontWeight: 500,
                  px: 2.5,
                  textTransform: 'none',
                }}
              >
                Back
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={selection === null}
              data-testid="add-yes-button-inline"
              sx={{
                height: 44,
                borderRadius: '12px',
                fontSize: 16,
                fontWeight: 600,
                px: 3,
                textTransform: 'none',
              }}
            >
              Save &amp; Next Question
            </Button>
          </Box>
        </ContentCard>
      </Box>

      {/* Mobile-only footer */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <InterviewFooter
          primaryLabel="Save & Next Question"
          onPrimary={handleSave}
          primaryDisabled={selection === null}
          primaryTestId="add-yes-button"
          tertiaryLabel={showBack ? 'Back' : undefined}
          onTertiary={showBack ? handleBack : undefined}
          tertiaryTestId="add-back-button"
        />
      </Box>
    </Box>
  );
}
